import { css } from '@emotion/react';
import { Link, Typography } from '@mui/material';
import { FC, ReactNode, useEffect } from 'react';
import { ForesightSupportEmail } from '../../constants/foresight';
import { colors } from '../../theme/colors';
import { elevatedCardStyleCss } from '../../theme/component-styles';
import { GenericLoadingFallback } from '../Fallback/GenericLoadingFallback';
import { trackEvent } from '../../util/datadogTracking';
import { suppressTracking } from '../../util/trackingSuppression';
import { FROSTED_NOTICE, FrostTarget } from './frosted-gate.constants';
import { useFrostState } from './useIsFrosted';

// Soft enough that the sample content reads as populated, too soft to read.
const FROST_BLUR_PX = 2;
const FROST_WASH_ALPHA = '59';

const gateCss = css`
  display: grid;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
`;

// Both layers share the single grid cell, so the frost covers exactly the preview's box.
const layerCss = css`
  grid-area: 1 / 1;
  min-width: 0;
  min-height: 0;
`;

const underlayCss = css`
  overflow: hidden;
  user-select: none;
`;

const frostCss = css`
  display: grid;
  place-items: center;
  padding: 1rem;
  z-index: 1;
  background: ${colors.primary[0]}${FROST_WASH_ALPHA};
  backdrop-filter: blur(${FROST_BLUR_PX}px);
`;

const noticeCss = css`
  ${elevatedCardStyleCss};
  display: grid;
  gap: 0.25rem;
  max-width: 26rem;
  padding: 1rem 1.5rem;
  text-align: center;
  background: ${colors.primary[0]};
`;

interface FrostedGateProps {
  target: FrostTarget;
  /** Rendered under the frost instead of the children; the live children never mount while frosted. */
  preview?: ReactNode;
  children: ReactNode;
}

/**
 * Shows a screen or card as a locked preview: sample content under a blurred,
 * non-interactive layer with a short notice. Off, it is transparent.
 */
export const FrostedGate: FC<FrostedGateProps> = ({ target, preview, children }) => {
  const frosted = useFrostState(target);
  // Deciding before the site's plan loads would flash a paid module frosted or an unpaid one live.
  if (frosted === undefined) {
    return <GenericLoadingFallback />;
  }
  if (!frosted) {
    return <>{children}</>;
  }
  return <FrostedLayers target={target}>{preview ?? children}</FrostedLayers>;
};

const FrostedLayers: FC<{ target: FrostTarget; children: ReactNode }> = ({ target, children }) => {
  // The view itself is reported; whatever the live components under the frost would report is not.
  useEffect(() => {
    trackEvent('gate.frosted_viewed', { target });
    return suppressTracking();
  }, [target]);

  return (
    <div css={gateCss} data-testid='frosted-gate' data-frost-target={target}>
      <div css={[layerCss, underlayCss]} inert aria-hidden='true' data-testid='frosted-underlay'>
        {children}
      </div>
      <div css={[layerCss, frostCss]}>
        <section css={noticeCss} aria-label={FROSTED_NOTICE.title}>
          <Typography variant='subtitle1' component='h3'>
            {FROSTED_NOTICE.title}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {FROSTED_NOTICE.message}
          </Typography>
          <Link href={`mailto:${ForesightSupportEmail}`} variant='body2'>
            {FROSTED_NOTICE.action}
          </Link>
        </section>
      </div>
    </div>
  );
};
